/**
 * Simple Theme Provider for Vite
 * 
 * Lightweight alternative to next-themes for Vite apps
 */

/* eslint-disable react-refresh/only-export-components */

import React, { createContext, useContext, useLayoutEffect, useState } from 'react';

type Theme = 'dark' | 'light' | 'system';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: Exclude<Theme, 'system'>;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Dark mode removed: always use light theme and no-op setter.
  const theme: Theme = 'light';
  const resolvedTheme: Exclude<Theme, 'system'> = 'light';
  const setTheme = (_: Theme) => {
    // intentionally no-op
  };

  // Ensure document reflects light theme for legacy selectors that check dataset/class
  try {
    if (typeof document !== 'undefined') {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
      document.documentElement.dataset.theme = 'light';
    }
  } catch {}

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    // Return default value instead of throwing during initial render
    return { theme: 'system' as Theme, resolvedTheme: 'light' as const, setTheme: () => {} };
  }
  return context;
}
